import styled, { css } from 'styled-components';

import VdpBox from '../../../_shared/components/VdpBox';
import Box from '../../../_shared/components/Box';
import Grid from '../../../_shared/components/Grid';
import Label from '../../../_shared/components/Label';
import Button from '../../../_shared/components/Button';
import Detail from '../../../_shared/components/Detail';
import Badge from '../../../_shared/components/Badge';
import applyResponsiveVdpStyles from '../../../_shared/helpers/applyResponsiveVdpStyles';
import isOmpPlatform from '../../../_shared/utils/isOmpPlatform';

const isOmp = isOmpPlatform();

/**
 * Variant Definitions
 */
const StyledHistoryVariants = {
  expanded: css`
    flex-wrap: wrap;
  `
};

const StyledHistoryHeadingVariants = {
  mini: css`
    padding: 0 2px;
    margin-bottom: 5px;
    order: 1;
  `
};

const StyledHistorySubHeadingVariants = {
  mini: css`
    width: 100%;
    padding: 0 2px;
    margin-top: 5px;
    order: 4;
  `,
  expanded: css`
    margin-left: 5px;
  `
};

const StyledBoxVariants = {
  mini: css`
    padding: 0 2px;
    flex-wrap: wrap;
  `,
  expanded: css`
    padding-right: 24px;
    margin-bottom: 27px;
  `
};

const StyledHistoryGroupingsVariants = {
  mini: css`
    padding: 0 2px;
    grid-template-columns: 1fr;
    grid-row-gap: inherit;
  `,
  expanded: css`
    padding: 0 40px 0 0;
    grid-template-columns: 1fr 1fr 1fr 1fr;
  `
};

const StyledDividerVariants = {
  mini: css`
    display: block;
  `,
  expanded: css`
    display: none;
  `
};

const StyledHistoryButtonVariants = {
  mini: css`
    font-size: 10px;
    width: 154px;
    order: 3;
  `,
  expanded: css`
    position: absolute;
    font-size: 14px;
    width: 170px;
  `
};

const HistoryTypeVariants = {
  mini: css`
    padding-top: 20px;
    width: 100%;
    flex-direction: row;
    display: flex;
    font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
    align-items: baseline;
  `,
  expanded: css`
    font-size: ${({ theme }) => theme.FONT_SIZES.MEDIUM};
  `
};

const HistoryDisclosuresNoneVariants = {
  mini: css`
    margin-left: auto;
    white-space: nowrap;
    padding-top: 20px;
  `,
  expanded: css`
    padding-top: 0;
  `
};

const StyledDisclosureCountBadgeVariants = {
  mini: css`
    order: 2;
  `
};

const StyledDisclosuresVariants = {
  mini: css`
    margin: 18px 0 6px 0;
  `
};

const HistoryItemContainerVariants = {
  mini: css`
    flex-direction: row;
  `,
  expanded: css`
    flex-direction: column;
    gap: 12px;
  `
};

/**
 * Styled Components
 */
const StyledHistory = styled(VdpBox)`
  display: flex;
  flex-direction: column;
  width: 100%;
  scroll-margin-top: 175px;
  position: relative;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      expanded: StyledHistoryVariants.expanded
    })}
`;

export const StyledHistoryHeading = styled.h2`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.LARGE};
  color: ${({ theme }) => theme.COLORS.BASE_INVERTED};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledHistoryHeadingVariants.mini
    })}
`;

export const StyledHistorySubHeading = styled(Label)`
  font-size: 16px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledHistorySubHeadingVariants.mini,
      expanded: StyledHistorySubHeadingVariants.expanded
    })}
`;

export const StyledBox = styled(Box)`
  display: flex;
  width: 100%;
  align-items: center;
  flex-direction: row;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledBoxVariants.mini,
      expanded: StyledBoxVariants.expanded
    })}
`;

export const StyledHistoryGroupings = styled(Grid)`
  grid-column-gap: 48px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledHistoryGroupingsVariants.mini,
      expanded: StyledHistoryGroupingsVariants.expanded
    })}
`;

export const StyledHistoryTitle = styled(Box)`
  display: flex;
  padding: 0 10px 0 0;
  flex-wrap: wrap;
  flex-direction: column;
`;

export const StyledDivider = styled.div`
  border-top: 1px solid
    ${({ theme }) =>
      isOmp ? theme.COLORS.NEUTRAL_110 : theme.COLORS.NEUTRAL_10};
  width: 100%;
  margin-top: 18px;
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDividerVariants.mini,
      expanded: StyledDividerVariants.expanded
    })}
`;

export const StyledHistoryButton = styled(Button.Transparent)`
  height: 36px;
  border: 2px solid ${({ theme }) => theme.COLORS.BASE_INVERTED};
  box-sizing: border-box;
  border-radius: 4px;
  margin-left: auto;
  align-items: center;
  flex-direction: row;
  display: flex;
  top: 30px;
  right: 36px;

  &:hover {
    color: ${({ theme }) => theme.COLORS.DARK_BLUE};
    border: 2px solid ${({ theme }) => theme.COLORS.DARK_BLUE};
  }

  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledHistoryButtonVariants.mini,
      expanded: StyledHistoryButtonVariants.expanded
    })}
`;

export const HistoryItemContainer = styled.div`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: HistoryItemContainerVariants.mini,
      expanded: HistoryItemContainerVariants.expanded
    })}
`;

export const HistoryType = styled(Label.Bold)`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: HistoryTypeVariants.mini,
      expanded: HistoryTypeVariants.expanded
    })}
`;

export const StyledHistoryDisclosuresNone = styled(Detail.Label)`
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: HistoryDisclosuresNoneVariants.mini,
      expanded: HistoryDisclosuresNoneVariants.expanded
    })}
`;

export const StyledDisclosureCountBadge = styled(Badge)`
  border: 2px solid ${({ theme }) => theme.COLORS.ORANGE};
  color: ${({ theme }) => theme.COLORS.ORANGE};
  min-width: 36px;
  border-radius: 18px;
  text-align: center;
  margin: 0 12px;
  max-height: ${({ theme }) => theme.FONT_SIZES.LARGE};
  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDisclosureCountBadgeVariants.mini
    })}
`;

export const StyledDisclosures = styled.span`
  font-family: ${({ theme }) => theme.FONT.REGULAR};
  font-size: ${({ theme }) => theme.FONT_SIZES.BASE};
  line-height: ${({ theme }) => theme.FONT_SIZES.BASE};
  flex-direction: column;
  background-color: ${({ theme }) => theme.COLORS.BASE};
  margin-right: 12px;
  position: relative;

  &:before {
    position: absolute;
    left: -1px;
    content: ' ';
    border-radius: 3px;
    background: ${({ theme }) => theme.COLORS.ORANGE};
    width: 4px;
    height: 100%;
  }

  ${({ theme }) =>
    applyResponsiveVdpStyles(theme, {
      mini: StyledDisclosuresVariants.mini
    })}
`;

export const StyledDisclosuresLabel = styled(Detail.Value)`
  padding-bottom: 12px;
  padding-left: 12px;

  &:only-child {
    padding-bottom: 0;
  }

  &:last-child {
    padding-bottom: 0;
  }

  &:not(:only-child):not(:last-child) {
    padding-bottom: 12px;
  }
`;

export const StyledDisclosureIcon = styled.img`
  margin-left: 5px;
`;

export default StyledHistory;
